<template>
    <div v-if="isVisible && isOpen && isMobile" class="fixed top-0 left-0 w-full h-full"
        style="background-color: rgba(0,0,0,0.4); z-index: 998;" @click="toggleSidebar">
    </div>

    <div v-if="isVisible"
        class="sidebar-menu flex flex-column surface-card surface-border transition-all transition-duration-300 flex-shrink-0 border-round-lg"
        :class="[isOpen ? 'w-14rem' : 'w-4rem', { 'mobile-open': isOpen }]" style="height: 100%;">

        <div class="flex align-items-center justify-content-center h-6rem surface-border flex-shrink-0 overflow-hidden">
            <div class="flex align-items-center justify-content-center w-full h-full">
                <img src="@/assets/MARCA-STED.png" alt="STED logo" class="transition-all transition-duration-300 mt-1"
                    :style="{ width: isOpen ? '140px' : '60px', height: isOpen ? '90px' : '50px', objectFit: 'contain' }" />
            </div>
        </div>

        <div class="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <Menu :model="menuItems" class="w-full border-none bg-transparent p-0">
                <template #item="{ item, props }">
                    <router-link v-if="item.to" :to="item.to" custom v-slot="{ href, navigate, isActive }">
                        <a :href="href" @click="(e) => { navigate(e); handleMobileClick() }" class="nav-link"
                            :class="{ 'active-link': isActive || isActiveRoute(item.to) }">
                            <div class="flex align-items-center flex-1">
                                <i :class="[item.icon, 'text-xl']"></i>
                                <span v-show="isOpen" class="font-medium text-sm white-space-nowrap ml-3">{{ item.label
                                }}</span>
                            </div>
                            <Badge v-if="item.badge" :value="item.badge" severity="danger" class="badge-pendentes"
                                :class="{ 'absolute top-0 right-0 m-1': !isOpen }" />
                        </a>
                    </router-link>
                    <a v-else class="nav-link cursor-pointer" @click="handleMenuCommand(item)">
                        <i :class="[item.icon, 'text-xl']"></i>
                        <span v-show="isOpen" class="font-medium text-sm white-space-nowrap ml-3">{{ item.label
                        }}</span>
                    </a>
                </template>
            </Menu>
        </div>

        <!-- Mobile Actions -->
        <div v-if="isOpen" class="md:hidden border-top-1 surface-border p-3 mt-auto">
            <div class="flex align-items-center gap-3 mb-3 cursor-pointer hover:surface-hover p-2 border-round transition-colors transition-duration-200"
                @click="router.push('/conta'); handleMobileClick()">
                <Avatar :label="userInitials" class="surface-200 text-color" size="large" shape="circle" />
                <div class="flex flex-column">
                    <span class="font-bold text-color">{{ authStore.user?.name }}</span>
                    <span class="text-sm text-color-secondary">{{ authStore.activeRole }}</span>
                </div>
            </div>

            <div class="flex flex-column gap-2">
                <RoleSwitcher class="w-full" />
                <Button :icon="themeToggleIcon" label="Alternar Tema" outlined class="w-full justify-content-start"
                    @click="toggleTheme" />
            </div>
        </div>

        <button @click="toggleSidebar"
            class="absolute flex align-items-center justify-content-center surface-card border-1 surface-border border-circle cursor-pointer shadow-2 text-500 hover:text-primary transition-colors transition-duration-200 outline-none hidden md:flex"
            style="top: 50%; right: -15px; width: 30px; height: 30px; transform: translateY(-50%); z-index: 1000;">
            <i :class="isOpen ? 'pi pi-angle-left' : 'pi pi-angle-right'"></i>
        </button>
    </div>
</template>

<script setup lang="ts">
import RoleSwitcher from '@/components/RoleSwitcher.vue'
import { usePermissions } from '@/composables/usePermissions'
import { useCoordenadorAvisosBadge } from '@/composables/coordenador/useCoordenadorAvisosBadge'
import { useDefesaRevisao } from '@/composables/defesas/useDefesaRevisao'
import { useCursoStore } from '@/stores/CursoStore'
import { useLogout } from '@/composables/useLogout'
import { useAuthStore } from '@/stores/AuthStore'
import { useThemeStore } from '@/stores/ThemeStore'
import Avatar from 'primevue/avatar'
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import { useConfirm } from 'primevue/useconfirm'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const confirm = useConfirm()
const { sair } = useLogout()
const { can } = usePermissions()

const themeToggleIcon = computed(() => themeStore.isDark ? "pi pi-sun" : "pi pi-moon")
const toggleTheme = () => themeStore.toggleTheme()

const userInitials = computed(() => {
    const name = authStore.user?.name || 'U';
    return name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
});

const isMobile = ref(window.innerWidth <= 768)
const isOpen = ref(!isMobile.value)

const handleResize = () => {
    const mobile = window.innerWidth <= 768
    if (mobile !== isMobile.value) {
        isMobile.value = mobile
        isOpen.value = !mobile
    }
}

const handleMobileClick = () => {
    if (isMobile.value) {
        isOpen.value = false
    }
}

const handleMenuCommand = (item: any) => {
    if (typeof item?.command === 'function') {
        item.command({ originalEvent: new MouseEvent('click'), item } as any)
    }
}

onMounted(() => {
    window.addEventListener('resize', handleResize)
    if (authStore.isOrientador) {
        carregarDefesas()
    }
    if (isPerfilCoordenacao.value) {
        carregarResumoAvisos()
    }
})

onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
})

const { totalPendentes, carregarDefesas } = useDefesaRevisao()
const defesasPendentesCount = computed(() => totalPendentes.value)
const { totalAvisos, carregarResumoAvisos } = useCoordenadorAvisosBadge()
const isPerfilCoordenacao = computed(() => authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc)

const dashboardMenuText = computed(() => {
    if (authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc) {
        return 'Gerenciar trabalhos'
    }
    if (authStore.isAluno) {
        return 'Meus Projetos'
    }
    return 'Modelos/Projetos'
})

const menuItems = computed(() => {
    const items = []

    // Admin
    if (authStore.isAdmin) {
        items.push({
            label: 'Administração',
            icon: 'pi pi-shield',
            to: '/admin'
        })
    }

    // Orientador
    if (authStore.isOrientador && !authStore.isAdmin && !authStore.isCoordenador && !authStore.isSecretaria) {
        items.push({
            label: 'Painel Orientador',
            icon: 'pi pi-briefcase',
            to: '/painel-orientador'
        })
        items.push({
            label: 'Revisão Defesas',
            icon: 'pi pi-file-check',
            to: '/defesas',
            badge: defesasPendentesCount.value > 0 ? defesasPendentesCount.value : null
        })
    }

    // Coordenador, Secretaria e Coordenador TCC (Gestão de Normas)
    if (authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc) {
        items.push({
            label: 'Gerenciar trabalhos',
            icon: 'pi pi-briefcase',
            to: '/gerenciar-trabalhos'
        })
        if (!authStore.isCoordenadorTcc) {
            items.push({
                label: 'Gerenciar Professores',
                icon: 'pi pi-users',
                to: '/gerenciar-professores'
            })
        }
        items.push({
            label: 'Normas TCC / Links',
            icon: 'pi pi-book',
            to: '/gerenciar-normas-tcc'
        })
        items.push({
            label: 'Avisos',
            icon: 'pi pi-bell',
            to: '/avisos',
            badge: totalAvisos.value > 0 ? totalAvisos.value : null
        })
    }

    // Aluno
    if (authStore.isAluno && !authStore.isAdmin && !authStore.isCoordenador && !authStore.isSecretaria && !authStore.isOrientador) {
        items.push({
            label: dashboardMenuText.value,
            icon: 'pi pi-folder',
            to: '/projetos'
        })
    }

    // User Normal
    if (authStore.isUserNormal && !authStore.isAdmin && !authStore.isCoordenador && !authStore.isSecretaria && !authStore.isOrientador && !authStore.isAluno) {
        items.push({
            label: 'Meus Projetos',
            icon: 'pi pi-folder',
            to: '/projetos'
        })
    }

    // Acompanhamento
    if (authStore.isAluno && !authStore.isOrientador) {
        items.push({
            label: 'Acompanhamento',
            icon: 'pi pi-list-check',
            to: '/acompanhamento'
        })
    }

    // Normas TCC / Links (leitura — APENAS para Alunos)
    if (authStore.isAluno && !authStore.isCoordenador && !authStore.isSecretaria && !authStore.isCoordenadorTcc) {
        items.push({
            label: 'Normas TCC / Links',
            icon: 'pi pi-book',
            to: '/normas-tcc'
        })
    }

    // Events
    items.push({
        label: 'Eventos',
        icon: 'pi pi-calendar',
        to: '/eventos'
    })

    // Comuns
    items.push({
        label: 'Tutoriais',
        icon: 'pi pi-video',
        to: '/tutoriais'
    })

    // Log do sistema (fora do if de admin: a permissão existe separada
    // justamente para poder ser concedida a quem não é admin, ex.: coordenador).
    // Fica por último, depois dos Tutoriais: é consulta ocasional, não rotina.
    if (can('sistema.logs')) {
        items.push({
            label: 'Log do sistema',
            icon: 'pi pi-history',
            to: '/logs'
        })
    }

    items.push({
        label: 'Sair',
        icon: 'pi pi-sign-out',
        command: handleLogout
    })

    return items
})

const visibleRoutes = ['/projetos', '/eventos', '/tutoriais', '/acompanhamento', '/conta', '/gerenciar-trabalhos', '/gerenciar-professores', '/gerenciar-normas-tcc', '/avisos', '/normas-tcc', '/painel-orientador', '/defesas', '/admin', '/logs', '/coordenador']

const isVisible = computed(() => {
    // Ocultar sidebar em todas as rotas do editor
    if (route.name === 'editor' || route.name === 'orientador-editor' || route.path.includes('/editor/')) {
        return false
    }

    return visibleRoutes.some(routePath =>
        route.path === routePath || route.path.startsWith(routePath)
    )
})

const cursoStore = useCursoStore()
watch(() => cursoStore.cursoAtual?.id, () => {
    if (isPerfilCoordenacao.value) {
        carregarResumoAvisos()
    }
})

watch(() => route.path, (newPath, oldPath) => {
    if (isPerfilCoordenacao.value && oldPath !== newPath) {
        carregarResumoAvisos()
    }
    if (authStore.isOrientador) {
        if (oldPath === '/orientador/defesas' && newPath !== '/orientador/defesas') {
            carregarDefesas()
        }
        if (newPath.startsWith('/orientador') && oldPath !== newPath) {
            carregarDefesas()
        }
    }
})

const isActiveRoute = (routePath: string): boolean => {
    if (routePath === '/projetos' && route.path === '/projetos') {
        return true
    }

    if (routePath === '/painel-orientador') {
        return route.path === '/painel-orientador'
    }

    if (routePath === '/defesas') {
        return route.path === '/defesas'
    }

    if (routePath === '/gerenciar-trabalhos') {
        return route.path === '/gerenciar-trabalhos' || route.path.startsWith('/gerenciar-trabalhos/')
    }

    if (routePath === '/gerenciar-professores') {
        return route.path === '/gerenciar-professores'
    }

    if (routePath === '/admin') {
        return route.path === '/admin' || route.path.startsWith('/admin/')
    }
    return route.path === routePath || route.path.startsWith(routePath + '/')
}

const handleLogout = (): void => {
    confirm.require({
        message: 'Deseja realmente sair do sistema?',
        header: 'Confirmar Saída',
        style: { width: '500px', height: '163px' },
        pt: {
            closeButton: {
                root: { class: 'hidden' }
            }
        },
        rejectProps: {
            label: 'Cancelar',
            style: {
                backgroundColor: 'white',
                borderColor: '#822133',
                color: '#822133'
            }
        },
        acceptProps: {
            label: 'Sair',
            style: {
                backgroundColor: '#822133',
                borderColor: '#822133',
                color: 'white'
            }
        },
        accept: async () => {
            await sair()
            confirm.close()
        }
    } as any)
}

const toggleSidebar = (): void => {
    isOpen.value = !isOpen.value
}

defineExpose({
    toggleSidebar,
    isOpen,
    isVisible
})
</script>

<style scoped>
.sidebar-menu {
    position: relative;
}

:deep(.role-switcher .p-button) {
    width: 100%;
    justify-content: flex-start;
}

.nav-link {
    display: flex;
    align-items: center;
    padding: 0.7rem 1rem;
    color: var(--text-color-secondary);
    text-decoration: none;
    transition: all 0.2s;
    border-radius: 6px;
}

.nav-link:hover {
    background-color: var(--surface-hover);
    color: var(--primary-color);
}

.nav-link.active-link {
    background-color: var(--surface-100) !important;
    color: #822133;
    font-weight: 600;
    border-left: 4px solid #822133;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.badge-pendentes {
    background-color: #822133 !important;
    color: white !important;
}

@media (max-width: 768px) {
    .sidebar-menu {
        position: fixed !important;
        left: 0;
        top: 0;
        height: 100vh !important;
        transform: translateX(-100%);
        z-index: 999;
    }

    .sidebar-menu.mobile-open {
        transform: translateX(0);
    }
}
</style>
